import { AuthProvider, useAuth } from './auth/AuthContext';
import { LoginPage } from './auth/LoginPage';
import { useWorkspaceBySlug, WorkspacesProvider } from './api/WorkspacesContext';
import { MapProvider } from './map/MapContext';
import { MapView } from './map/MapView';
import { SceneManager } from './components/SceneManager/SceneManager';
import { TopBar } from './components/TopBar/TopBar';
import { NavigationProvider, useNavigation } from './navigation/NavigationContext';
import { usePageTitle } from './navigation/usePageTitle';
import { HomePage } from './pages/HomePage/HomePage';
import './styles/app.css';

// The map viewer itself (top bar + map + Scene Manager) — shown once
// navigated into a workspace. Split out so AppBody can pick between this
// and HomePage without re-mounting MapProvider (map state, e.g. drawn
// measurements, survives navigating home and back).
function MapViewerShell() {
  return (
    <div className="app-shell">
      <TopBar />
      <MapView />
      <SceneManager />
    </div>
  );
}

function AppBody() {
  const { view, homeNav, focusGroupId } = useNavigation();
  const workspace = useWorkspaceBySlug(focusGroupId);
  // Tab title follows the page: Workspaces / Content on the home page, the workspace's own
  // name on the map (falls back to "Map" while the workspace list loads or for an unknown slug).
  usePageTitle(view === 'home' ? (homeNav === 'content' ? 'Content' : 'Workspaces') : (workspace?.title ?? 'Map'));
  return view === 'home' ? <HomePage /> : <MapViewerShell />;
}

function Shell() {
  const { status, retry } = useAuth();
  // Signed-in pages set their own title (AppBody); the states before that are covered here.
  usePageTitle(
    status === 'anonymous' ? 'Sign in' : status === 'unavailable' ? 'Sign-in unavailable' : status === 'checking' || status === 'redirecting' ? 'Signing in' : null,
  );

  if (status === 'checking' || status === 'redirecting') {
    return <div className="app-loading">Checking session…</div>;
  }

  if (status === 'anonymous') {
    return <LoginPage />;
  }

  // "We couldn't ASK whether you're signed in" is not "you're signed out" —
  // never show the login form for it (see auth/session.ts).
  if (status === 'unavailable') {
    return (
      <div className="app-loading app-unavailable" role="alert">
        <div className="app-unavailable-title">Couldn&rsquo;t reach the sign-in service</div>
        <p>The session check didn&rsquo;t get an answer from the server. Your sign-in state is unknown, not expired.</p>
        <button type="button" className="app-retry-btn" onClick={retry}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <WorkspacesProvider>
      <NavigationProvider>
        <MapProvider>
          <AppBody />
        </MapProvider>
      </NavigationProvider>
    </WorkspacesProvider>
  );
}

export function App() {
  return (
    <AuthProvider>
      <Shell />
    </AuthProvider>
  );
}
